import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../APIClient";

export enum MaritalStatus {
  SINGLE = "SINGLE",
  MARRIED = "MARRIED",
  RELATIONSHIP = "RELATIONSHIP",
  COMPLICATED = "COMPLICATED",
  NO_ANSWER = "NO_ANSWER",
}

export enum HasChildren {
  YES = "YES",
  NO = "NO",
  NO_ANSWER = "NO_ANSWER",
}

export enum GroupGenderPreference {
  ALL_GENDERS_OKAY = "ALL_GENDERS_OKAY",
  SAME_GENDER_PREFERRED = "SAME_GENDER_PREFERRED",
}

export enum GroupAgePreference {
  ALL_AGES_OKAY = "ALL_AGES_OKAY",
  SAME_AGE_PREFERRED = "SAME_AGE_PREFERRED",
}

// DTO for the personal information input
export interface PersonalInfoInputDto {
  gender: string;
  maritalStatus: MaritalStatus;
  hasChildren: HasChildren;
  profession: string;
  nationality: string;
  birthdate: string;
  groupGenderPreference: GroupGenderPreference;
  groupAgePreference: GroupAgePreference;
}

// DTO for the value of each answer
export interface AnswerDetailInputDto {
  answerOptions?: number[];
  answerScale?: number;
}

// DTO for the entire request payload
export interface SubmitMyProfileAndAnswersRequestDto {
  personal_info?: PersonalInfoInputDto;
  answers: Record<string, AnswerDetailInputDto>;
}

// Expected response from the API
export interface SubmitMyProfileAndAnswersResponseDto {
  message: string;
}

const submitMyProfileAndAnswers = async (
  data: SubmitMyProfileAndAnswersRequestDto
): Promise<SubmitMyProfileAndAnswersResponseDto> => {
  const response = await apiFetch("api/personality-test/me/profile-and-answers", {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("POST api/personality-test/me/profile-and-answers failed");
  }
  return response.json();
};

export function useSubmitMyProfileAndAnswers() {
  const queryClient = useQueryClient();

  return useMutation<
    SubmitMyProfileAndAnswersResponseDto,
    Error,
    SubmitMyProfileAndAnswersRequestDto
  >({
    mutationFn: submitMyProfileAndAnswers,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["myInfo"] });
      await queryClient.invalidateQueries({ queryKey: ["myPersonalityTestAnswers"] });
    },
    onError: (error) => {
      // Handle error, e.g., show a notification to the user
      console.error("Error submitting profile and answers:", error.message);
    },
  });
}
